/**
 * Поддерево `project.settings.navigation`: справочник типов указателей и его правка.
 *
 * Писателей у поддерева два — правая панель режима и агент MCP (docs/mcp/wayfinding.md, WR5),
 * — и оба пишут через `PATCH /:id/wayfinding/settings`. Сервер применяет правку к СВЕЖЕМУ
 * поддереву под блокировкой строки карты: веб присылает «было → стало» своей правки
 * (`mergeNavigationChange`), агент меняет прочитанное под той же блокировкой. Прежде веб
 * присылал поддерево целиком, и правка, сделанная после чтения кэша вкладки, молча
 * пропадала.
 */
import type { AutocraftGenerator } from '../types/autocraft.js';
import type { WayfindingSignType } from '../types/wayfinding.js';
import { WAYFINDING_PLAN_INDEXES_KEY } from './wayfindingSignNames.js';
import { WAYFINDING_SIGN_TYPE_COLORS } from './wayfindingSignTypes.js';

/**
 * Ключ в `settings.navigation`: хеши генераторов, под которые тип уже предлагали.
 *
 * Помнить это приходится отдельно: по одним типам не отличить генератор, который только что
 * появился в Автокрафте, от генератора, чей тип человек удалил как ненужный, — и каждое
 * «Проверить» возвращало бы удалённые типы.
 */
export const WAYFINDING_SEEN_GENERATORS_KEY = 'autocraftSeenGenerators';

/** Признак «режимом работали» — серверный, его ставит только `markWayfindingUsed`. */
export const WAYFINDING_USED_FLAG_KEY = 'enabled';

/** Предложенные генераторы из поддерева `navigation`; мусор и пустые строки отброшены. */
export function readSeenGenerators(navigation: Record<string, unknown>): string[] {
  const raw = navigation[WAYFINDING_SEEN_GENERATORS_KEY];
  return Array.isArray(raw)
    ? raw.filter((value): value is string => typeof value === 'string' && value !== '')
    : [];
}

export interface GeneratorSignTypesPlan {
  /** Генераторы, которым нужен тип, — в порядке списка (Автокрафт отдаёт по алфавиту). */
  generators: AutocraftGenerator[];
  /** Новый список предложенных; `null` — не изменился, писать нечего. */
  seen: string[] | null;
}

/**
 * Какие генераторы получат тип. Генератор, у которого тип уже есть (заведён руками или
 * «Сделать указателями»), тоже становится предложенным: смени человек этому типу генератор,
 * прежний не должен вернуться отдельным типом. Список предложенных копится, а не заменяется
 * нынешним: ссылку переключили на другой проект и обратно — удалённые типы первого не вернутся.
 */
export function planGeneratorSignTypes(
  types: ReadonlyArray<Pick<WayfindingSignType, 'autocraftGeneratorHash'>>,
  seen: readonly string[],
  generators: readonly AutocraftGenerator[],
): GeneratorSignTypesPlan {
  const seenSet = new Set(seen);
  const typed = new Set(
    types.flatMap((type) => (type.autocraftGeneratorHash ? [type.autocraftGeneratorHash] : [])),
  );
  const fresh: AutocraftGenerator[] = [];
  const added: string[] = [];
  for (const generator of generators) {
    if (!generator.hash || seenSet.has(generator.hash)) {
      continue;
    }
    seenSet.add(generator.hash);
    added.push(generator.hash);
    if (!typed.has(generator.hash)) {
      fresh.push(generator);
    }
  }
  return {
    generators: fresh,
    seen: added.length > 0 ? [...seen, ...added] : null,
  };
}

/**
 * Цвета пачки новых типов: сначала цвета палитры, которых в справочнике нет. По одному счёту
 * («цвет номер N по кругу») после удалённого типа новый вставал цветом соседа — а палитра
 * затем и нужна, чтобы типы на плане не сливались. Свободные кончились — по кругу.
 */
export function pickSignTypeColors(usedColors: readonly string[], count: number): string[] {
  const used = new Set(usedColors.map((color) => color.toLowerCase()));
  const free = WAYFINDING_SIGN_TYPE_COLORS.filter((color) => !used.has(color.toLowerCase()));
  return Array.from(
    { length: count },
    (_, index) =>
      free[index] ??
      WAYFINDING_SIGN_TYPE_COLORS[(usedColors.length + index) % WAYFINDING_SIGN_TYPE_COLORS.length]!,
  );
}

/**
 * Имя нового типа, не занятое в справочнике. С именем-основой («Настенный») занятое получает
 * номер — «Настенный 2»; без него — `defaultName(N)` («Тип N»), где N начинается с `count + 1`.
 * Подпись по умолчанию передаёт вызывающий: она на языке интерфейса.
 */
export function uniqueSignTypeName(
  base: string | undefined,
  names: ReadonlySet<string>,
  count: number,
  defaultName: (index: number) => string,
): string {
  const trimmed = base?.trim();
  if (trimmed) {
    let name = trimmed;
    for (let index = 2; names.has(name); index += 1) {
      name = `${trimmed} ${index}`;
    }
    return name;
  }
  let index = count + 1;
  let name = defaultName(index);
  while (names.has(name)) {
    index += 1;
    name = defaultName(index);
  }
  return name;
}

function newSignTypeId(): string {
  return `st-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

/**
 * Id, не занятый в справочнике. Пачка типов создаётся в одну миллисекунду, и совпадение
 * хвоста отбросило бы тип молча: `readWayfindingSignTypes` дубли `id` не пропускает.
 */
export function freshSignTypeId(taken: Set<string>): string {
  let id = newSignTypeId();
  while (taken.has(id)) {
    id = newSignTypeId();
  }
  taken.add(id);
  return id;
}

/* ------------------------------------------------------------------ */
/*  Слияние правки                                                     */
/* ------------------------------------------------------------------ */

type Json = Record<string, unknown>;

function readObject(value: unknown): Json {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Json) : {};
}

function sameJson(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/** Записи справочника как есть, по id; мусор без id отброшен (его не правит ни один писатель). */
function rawTypesById(navigation: Json): Map<string, Json> {
  const raw = navigation.signTypes;
  const byId = new Map<string, Json>();
  if (!Array.isArray(raw)) {
    return byId;
  }
  for (const item of raw) {
    const entry = readObject(item);
    if (typeof entry.id === 'string' && entry.id !== '' && !byId.has(entry.id)) {
      byId.set(entry.id, entry);
    }
  }
  return byId;
}

/** Применяет к `target` изменения ключей `base → next`; ключ, пропавший в `next`, снимается. */
function applyKeyDiff(target: Json, base: Json, next: Json, skip: ReadonlySet<string>): Json {
  const out: Json = { ...target };
  for (const key of new Set([...Object.keys(base), ...Object.keys(next)])) {
    if (skip.has(key) || sameJson(base[key], next[key])) {
      continue;
    }
    if (next[key] === undefined || next[key] === null) {
      delete out[key];
    } else {
      out[key] = next[key];
    }
  }
  return out;
}

const MERGED_SEPARATELY = new Set([
  WAYFINDING_USED_FLAG_KEY,
  'signTypes',
  WAYFINDING_PLAN_INDEXES_KEY,
  WAYFINDING_SEEN_GENERATORS_KEY,
]);

/**
 * Трёхстороннее слияние: правка писателя («было» `base` → «стало» `next`) поверх актуального
 * поддерева `current`.
 *
 * - Ключи верхнего уровня — по изменению: что писатель не трогал, остаётся как у `current`.
 * - Типы — по id и по полям: поле, которое писатель не менял, не откатывается чужой правкой.
 *   Новые типы дописываются в конец. Тип, который правка заводит синком «Проверить» под генератор,
 *   уже разобранный другим писателем после `base`, не дублируется.
 * - Индексы планов — по плану; предложенные генераторы — объединением (список только растёт).
 * - `enabled` не трогается никогда.
 */
export function mergeNavigationChange(current: unknown, base: unknown, next: unknown): Json {
  const cur = readObject(current);
  const was = readObject(base);
  const now = readObject(next);
  const out = applyKeyDiff(cur, was, now, MERGED_SEPARATELY);

  // Индексы планов.
  const indexes = applyKeyDiff(
    readObject(cur[WAYFINDING_PLAN_INDEXES_KEY]),
    readObject(was[WAYFINDING_PLAN_INDEXES_KEY]),
    readObject(now[WAYFINDING_PLAN_INDEXES_KEY]),
    new Set(),
  );
  if (Object.keys(indexes).length > 0) {
    out[WAYFINDING_PLAN_INDEXES_KEY] = indexes;
  } else {
    delete out[WAYFINDING_PLAN_INDEXES_KEY];
  }

  // Предложенные генераторы.
  const seenBefore = new Set(readSeenGenerators(was));
  const seenNow = readSeenGenerators(cur);
  const seen = [...seenNow];
  // Проверка по множеству, а не `seen.includes`: список копится, и поиск по массиву на каждый
  // хеш правки — квадрат под блокировкой строки карты.
  const seenSet = new Set(seen);
  for (const hash of readSeenGenerators(now)) {
    if (!seenSet.has(hash)) {
      seenSet.add(hash);
      seen.push(hash);
    }
  }
  if (seen.length > 0) {
    out[WAYFINDING_SEEN_GENERATORS_KEY] = seen;
  }
  /**
   * Генераторы, которые ЭТА правка разбирает как «Проверить» (добавляет в предложенные) и которые
   * уже разобрал другой писатель после `base`. Только их новые типы — дубль чужого синка: тип,
   * заведённый под генератор руками («Сделать указателями»), в предложенные не попадает и не
   * отбрасывается — на его id уже ссылается вызывающий.
   */
  const seenByThisChange = new Set(readSeenGenerators(now).filter((hash) => !seenBefore.has(hash)));
  const seenByOthers = new Set(seenNow.filter((hash) => !seenBefore.has(hash) && seenByThisChange.has(hash)));

  // Типы.
  const baseTypes = rawTypesById(was);
  const nextTypes = rawTypesById(now);
  const curTypes = rawTypesById(cur);
  if (baseTypes.size === 0 && nextTypes.size === 0) {
    return out;
  }
  const types: Json[] = [];
  for (const [id, type] of curTypes) {
    const before = baseTypes.get(id);
    const after = nextTypes.get(id);
    if (before && !after) {
      continue; // писатель удалил тип
    }
    types.push(before && after ? applyKeyDiff(type, before, after, new Set(['id'])) : type);
  }
  for (const [id, type] of nextTypes) {
    if (baseTypes.has(id) || curTypes.has(id)) {
      continue;
    }
    const generator = typeof type.autocraftGeneratorHash === 'string' ? type.autocraftGeneratorHash : '';
    if (generator && seenByOthers.has(generator)) {
      continue;
    }
    types.push(type);
  }
  if (types.length > 0) {
    out.signTypes = types;
  } else {
    delete out.signTypes;
  }
  return out;
}
